import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as BankIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import EmployeeService from '../../services/employee.service';
import PayrollService from '../../services/payroll.service';

const EmployeeView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [employee, setEmployee] = useState<any>(null);
  const [payrolls, setPayrolls] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [payrollsLoading, setPayrollsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchEmployee();
      fetchPayrolls();
    }
  }, [id]);

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await EmployeeService.getOne(Number(id), {
        populate: 'department',
      });
      setEmployee(response.data?.data);
    } catch (error: any) {
      console.error('Error fetching employee:', error);
      setError(error.response?.data?.error?.message || 'Failed to load employee');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrolls = async () => {
    try {
      setPayrollsLoading(true);
      const response = await PayrollService.getAll({
        'filters[employee][id][$eq]': Number(id),
        populate: ['employee', 'processedBy'],
        'pagination[pageSize]': 100,
        sort: 'period:desc',
      });
      setPayrolls(response.data?.data || []);
    } catch (error: any) {
      console.error('Error fetching payrolls:', error);
    } finally {
      setPayrollsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await EmployeeService.delete(Number(id));
        navigate('/employees');
      } catch (error) {
        console.error('Error deleting employee:', error);
        alert('Failed to delete employee');
      }
    }
  };

  const getEmployeeStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'onLeave':
        return 'warning';
      case 'suspended':
        return 'error';
      case 'terminated':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPayrollStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'processed':
        return 'info';
      case 'draft':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number, currency: string = 'OMR') => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'OMR',
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !employee) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/employees')}
          sx={{ mb: 2 }}
        >
          Back to Employees
        </Button>
        <Alert severity="error">
          {error || 'Employee not found'}
        </Alert>
      </Box>
    );
  }

  const data = employee.attributes;

  return (
    <Box>
      {/* Header */}
      <Stack spacing={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/employees')}
          sx={{ alignSelf: 'flex-start' }}
        >
          Back
        </Button>
        
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Typography variant="h4">
              {data.firstName} {data.lastName}
            </Typography>
            <Chip
              label={data.status || 'active'}
              color={getEmployeeStatusColor(data.status)}
              size="medium"
            />
          </Stack>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/employees/${id}/edit`)}
              fullWidth={isMobile}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              fullWidth={isMobile}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {/* Main Content */}
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Personal Information */}
          <Box flex={1}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Employee Number
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.employeeNumber || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Date of Birth
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(data.dateOfBirth)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      First Name
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.firstName || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Last Name
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.lastName || '-'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Gender
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.gender || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Marital Status
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.maritalStatus || '-'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.address || '-'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* Contact Information */}
          <Box flex={1}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.email || '-'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.phone || '-'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Emergency Contact
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.emergencyContact || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Employment Information */}
          <Box flex={1}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <WorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Employment Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Position
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.position || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Department
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.department?.data?.attributes?.name || '-'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      <CalendarIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Hire Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(data.hireDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      <CalendarIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Termination Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(data.terminationDate)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Contract Type
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.contractType || '-'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* Financial Information */}
          <Box flex={1}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <BankIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Financial Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Salary
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatCurrency(data.salary)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Tax ID
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.taxId || '-'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Bank Account
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.bankAccount || '-'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Social Security Number
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.socialSecurityNumber || '-'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Stack>

        {/* Payroll Information */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Payroll History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {payrollsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          ) : payrolls.length === 0 ? (
            <Alert severity="info">No payroll records found for this employee.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Base Salary</TableCell>
                    <TableCell align="right">Bonuses</TableCell>
                    <TableCell align="right">Deductions</TableCell>
                    <TableCell align="right">Overtime</TableCell>
                    <TableCell align="right">Tax</TableCell>
                    <TableCell align="right">Net Salary</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Work Days</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payrolls.map((payroll) => {
                    const attrs = payroll.attributes;
                    return (
                      <TableRow key={payroll.id} hover>
                        <TableCell>{attrs.period || '-'}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(attrs.baseSalary, attrs.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(attrs.bonuses || 0, attrs.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(attrs.deductions || 0, attrs.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(attrs.overtime || 0, attrs.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(attrs.tax || 0, attrs.currency)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(attrs.netSalary, attrs.currency)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={attrs.status || 'draft'}
                            color={getPayrollStatusColor(attrs.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(attrs.paymentDate)}</TableCell>
                        <TableCell>{attrs.workDays || '-'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit Payroll">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/payroll/${payroll.id}/edit`)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Additional Details */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box flex={1}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Employment Type
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.employmentType || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                    <Typography variant="body2" color="text.secondary">
                      Contract Type
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.contractType || '-'}
                    </Typography>
                  </Box>
                </Box>
                
                {data.emergencyContactName && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Emergency Contact Name
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.emergencyContactName}
                    </Typography>
                  </Box>
                )}
                
                {data.emergencyContactPhone && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Emergency Contact Phone
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.emergencyContactPhone}
                    </Typography>
                  </Box>
                )}
                
                {data.notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.notes}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

export default EmployeeView;

